<template>
    <section class="l2-feature-showcases" id="features">
        <div class="l2-feature-showcases__container">
            <div class="l2-feature-showcases__header">
                <h2 class="l2-feature-showcases__title">
                    Everything in one system.<br />
                    <span class="l2-feature-showcases__title-accent">Clear and connected.</span>
                </h2>
                <p class="l2-feature-showcases__subtitle">Four core workflows that keep the rest of your business running smoothly.</p>
            </div>

            <v-row justify="space-between" class="pl-md-16 px-4">
                <v-col cols="12" md="6" class="py-md-12 py-0 py-5"
                    ><LandingFeatureShowcaseRow :title="features[0].title" :bullets="features[0].bullets" :accent="features[0].accent"
                /></v-col>
                <v-col cols="12" md="6" class="py-md-12 py-5 pr-0"
                    ><LandingFeatureShowcaseRow :title="features[1].title" :bullets="features[1].bullets" :accent="features[1].accent"
                /></v-col>
            </v-row>
            <v-row class="pl-md-16 px-4">
                <v-col cols="12" md="6" class="py-md-12 py-0 py-5"
                    ><LandingFeatureShowcaseRow :title="features[2].title" :bullets="features[2].bullets" :accent="features[2].accent"
                /></v-col>
                <v-col cols="12" md="6" class="py-md-12 py-5 pr-0"
                    ><LandingFeatureShowcaseRow :title="features[3].title" :bullets="features[3].bullets" :accent="features[3].accent"
                /></v-col>
            </v-row>

            <div class="l2-feature-showcases__more">
                <div class="l2-feature-showcases__more-header">
                    <h2 class="l2-feature-showcases__more-title">
                        And so much more <span class="l2-feature-showcases__more-title-accent">waiting inside.</span>
                    </h2>
                    <p class="l2-feature-showcases__more-subtitle">
                        Mix n' Batch is loaded with advanced features that reveal themselves as you use it.
                    </p>
                </div>

                <v-row>
                    <v-col cols="12" md="6">
                        <ul class="l2-feature-showcases__more-list">
                            <li v-for="item in props.moreItems" :key="item.title" class="l2-feature-showcases__more-list-item pb-5">
                                <div class="l2-feature-showcases__more-icon">
                                    <v-icon :icon="item.icon" size="20" />
                                </div>
                                <div class="l2-feature-showcases__more-text">
                                    <b>{{ item.title }}</b>
                                    {{ ' ' }}
                                    {{ item.description }}
                                </div>
                            </li>
                        </ul>
                    </v-col>

                    <v-col cols="12" md="6">
                        <div class="d-flex flex-column ga-6">
                            <LandingMoreBox
                                :title="props.moreBoxes[0].title"
                                :subtitle="props.moreBoxes[0].subtitle"
                                :accent="props.moreBoxes[0].accent"
                                :image-src="props.moreBoxes[0].imageSrc"
                                :full-image-src="props.moreBoxes[0].fullImageSrc"
                                :image-alt="props.moreBoxes[0].imageAlt" />
                        </div>
                    </v-col>
                </v-row>
                <v-row align="stretch">
                    <v-col class="d-flex">
                        <LandingMoreBox
                            class="h-100 flex-grow-1"
                            :title="props.moreBoxes[1].title"
                            :subtitle="props.moreBoxes[1].subtitle"
                            :accent="props.moreBoxes[1].accent"
                            :image-src="props.moreBoxes[1].imageSrc"
                            :full-image-src="props.moreBoxes[1].fullImageSrc"
                            :image-alt="props.moreBoxes[1].imageAlt" />
                    </v-col>
                    <v-col class="d-flex">
                        <LandingMoreBox
                            class="h-100 flex-grow-1"
                            :title="props.moreBoxes[2].title"
                            :subtitle="props.moreBoxes[2].subtitle"
                            :accent="props.moreBoxes[2].accent"
                            :image-src="props.moreBoxes[2].imageSrc"
                            :full-image-src="props.moreBoxes[2].fullImageSrc"
                            :image-alt="props.moreBoxes[2].imageAlt" />
                    </v-col>
                </v-row>
            </div>
        </div>
    </section>
</template>

<script lang="ts" setup>
    import LandingFeatureShowcaseRow from './LandingFeatureShowcaseRow.vue';
    import LandingMoreBox from './LandingMoreBox.vue';

    export type LandingShowcaseFeature = {
        title: string;
        accent: string;
        bullets: string[];
    };

    export type LandingMoreItem = {
        icon: string;
        title: string;
        description: string;
    };

    export type LandingMoreBoxData = {
        title: string;
        subtitle: string;
        accent: string;
        imageSrc: string;
        fullImageSrc: string;
        imageAlt: string;
    };

    const props = defineProps<{
        features: LandingShowcaseFeature[];
        moreItems: LandingMoreItem[];
        moreBoxes: LandingMoreBoxData[];
    }>();
</script>

<style lang="scss" scoped>
    .l2-feature-showcases {
        position: relative;
        padding: var(--l2-section-padding-y) 24px;
        background: var(--l2-bg-elevated);
        overflow: hidden;
        --l2-feature-stagger: clamp(140px, 12vw, 260px);

        &::before {
            content: '';
            position: absolute;
            inset: 0;
            background:
                radial-gradient(ellipse 60% 40% at 20% 0%, rgba(167, 139, 250, 0.12), transparent 60%),
                radial-gradient(ellipse 60% 40% at 80% 100%, rgba(103, 232, 249, 0.08), transparent 60%);
            pointer-events: none;
        }

        &__container {
            position: relative;
            max-width: 1280px;
            margin: 0 auto;
            z-index: 1;
        }

        &__header {
            text-align: center;
            margin-bottom: 60px;
            max-width: 720px;
            margin-left: auto;
            margin-right: auto;
        }

        &__title {
            font-size: clamp(2rem, 4vw, 3rem);
            font-weight: 700;
            line-height: 1.2;
            color: var(--l2-text);
            margin-bottom: 16px;
        }

        &__title-accent {
            color: var(--landing-muted);
        }

        &__subtitle {
            font-size: 1.0625rem;
            line-height: 1.7;
            color: var(--landing-muted);
            margin: 0;
        }

        &__callouts {
            position: relative;
            display: grid;
            grid-template-columns: 1fr;
            gap: 20px;
        }

        &__callout {
            position: relative;

            &--left,
            &--right {
                max-width: 640px;
            }

            @media (min-width: 960px) {
                &--left {
                    justify-self: start;
                    margin-bottom: var(--l2-feature-stagger);
                }

                &--right {
                    justify-self: end;
                    transform: translateY(var(--l2-feature-stagger));
                }
            }
        }

        &__more {
            margin-top: 140px;
        }

        &__more-header {
            text-align: center;
            margin-bottom: 48px;
            max-width: 820px;
            margin-left: auto;
            margin-right: auto;
        }

        &__more-title {
            font-size: clamp(2rem, 4vw, 3rem);
            font-weight: 700;
            line-height: 1.2;
            color: var(--l2-text);
            margin: 0 0 16px;

            @media (min-width: 600px) {
                white-space: nowrap;
            }
        }

        &__more-title-accent {
            color: var(--landing-muted);
        }

        &__more-subtitle {
            font-size: 1.0625rem;
            line-height: 1.7;
            color: var(--l2-text-muted);
            margin: 0;
        }

        &__more-list {
            margin: 0;
            padding: 0;
            list-style: none;
            display: flex;
            flex-direction: column;
            gap: 14px;
        }

        &__more-list-item {
            display: grid;
            grid-template-columns: 24px 1fr;
            gap: 12px;
            align-items: start;
        }

        &__more-icon {
            width: 24px;
            height: 24px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: var(--l2-accent-soft);
            border: 1px solid var(--l2-border);
            border-radius: 10px;
        }

        &__more-text {
            color: var(--l2-text-muted);
            line-height: 1.6;

            b {
                color: var(--l2-text);
                font-weight: 700;
            }
        }
    }

    @media (max-width: 600px) {
        .l2-feature-showcases {
            &__header {
                margin-bottom: 36px;
            }

            &__more {
                margin-top: 80px;
            }

            &__more-header {
                margin-bottom: 32px;
            }
        }
    }
</style>
